import { Injectable, NgZone, Inject } from '@angular/core';
import * as ethUtil from 'ethereumjs-util';
import { BehaviorSubject, Observable, Subscription, of, from, throwError, interval, forkJoin, EMPTY, combineLatest } from 'rxjs';
import { map, catchError, tap, filter, take, concatMap, takeWhile, endWith, mergeMap, delay, shareReplay } from 'rxjs/operators';

import { CONFIGURATION, Configuration, Network } from './configuration';
import BigNumber from 'bignumber.js';
import { sharePublishReplay } from 'ngx-libs';

import { Web3 } from 'web3';
import { EIP712Signer } from '../models/eip712-signer';

// const Web3 = require('web3');
const HDWalletProvider = require('@truffle/hdwallet-provider');
// const MultiHttpProvider = require('./multi-http-provider');

function enumToNames(e: any) {
  return Object.keys(e).filter(k => isNaN(parseInt(k, 10)));
}

export interface Signature {
  v: number;
  r: string;
  s: string;
}

export enum Wallet {
  None,
  Software,
  PrivateKey,
  Default,
  Okx
}

export enum Software {
  None,
  Unknown,
  Metamask,
  Trust,
  ImToken,
  Status,
  Toshi,
  Blocto
}

export enum Status {
  Disconnected,
  Connecting,
  Connected
}

export enum Error {
  NoSoftware,
  InvalidPrivateKey
}

export const WalletNames = enumToNames(Wallet);
export const SoftwareNames = enumToNames(Software);
export const StatusNames = enumToNames(Status);
export const ZeroAddress = '0x0000000000000000000000000000000000000000';

@Injectable({
  providedIn: 'root'
})
export class EthWalletService {
  private statusSubject = new BehaviorSubject(Status.Disconnected);
  private blockNumberSubject = new BehaviorSubject(0);
  private chainIdSubject = new BehaviorSubject(0);
  private softwareSubject = new BehaviorSubject(Software.Unknown);
  private walletSubject = new BehaviorSubject(Wallet.None);
  private web3Subject = new BehaviorSubject(null);
  private connectWatchBlock: Subscription;
  private selectedNetworkSubject = new BehaviorSubject<Network>(null);

  status = this.statusSubject.asObservable();
  blockNumber = this.blockNumberSubject.asObservable();
  chainId = this.chainIdSubject.asObservable();
  software = this.softwareSubject.asObservable();
  wallet = this.walletSubject.asObservable();
  web3 = this.web3Subject.asObservable();
  readonlyWeb3: Observable<any>;
  chainMatched: Observable<boolean>;
  selectedNetwork = this.selectedNetworkSubject.asObservable().pipe(delay(1));

  constructor(private ngZone: NgZone, @Inject(CONFIGURATION) private configuration: Configuration) {
    this.selectedNetworkSubject.next(this.configuration.networks[0]);
    this.readonlyWeb3 = this.selectedNetworkSubject.pipe(
      map((network) => this.createWeb3({
        host: network.params.rpcUrls[0]
      })),
      shareReplay(1)
    );
    this.chainMatched = combineLatest([this.selectedNetwork, this.status, this.chainId]).pipe(
      map(([network, s, chainId]) => s !== Status.Connected || parseInt(network.params.chainId, 16) === chainId),
      sharePublishReplay(1)
    );
  }

  getReadonlyWeb3(network: Network) {
    return this.createWeb3({
      host: network.params.rpcUrls[0]
    });
  }

  selectNetwork(network: Network) {
    if (this.selectedNetworkSubject.value !== network) {
      this.selectedNetworkSubject.next(network);
    }
  }

  getAccounts(): Observable<string[]> {
    if (this.walletSubject.value === Wallet.Default) {
      return of([]);
    } else {
      return this.web3Execute<string[]>(false, (web3) => web3.eth.getAccounts());
    }
  }

  getCurrentBlock(readonly = false) {
    return this.web3Execute<any>(readonly, (web3) => web3.eth.getBlock('latest'));
  }

  private getBlockNumber(readonly = false) {
    return this.web3Execute<number>(readonly, (web3) => web3.eth.getBlockNumber()).pipe(map((v: any) => Number(v)));
  }

  getBlock(hashOrNumber: number | string, readonly = false) {
    return this.web3Execute<any>(readonly, (web3) => web3.eth.getBlock(hashOrNumber));
  }

  getTransaction(txId: string, readonly = false) {
    return this.web3Execute<any>(readonly, (web3) => web3.eth.getTransaction(txId));
  }

  getTransactionInfo(txId: string, readonly = false) {
    return this.web3Execute<any>(readonly, (web3) => web3.eth.getTransactionReceipt(txId));
  }

  getBalance(address: string, readonly = false) {
    return this.web3Execute<any>(readonly, (web3) => web3.eth.getBalance(address)).pipe(
      map((v: any) => new BigNumber(v))
    );
  }

  getChainId(readonly = false) {
    return this.web3Execute<number>(readonly, (web3) => web3.eth.getChainId()).pipe(map((v) => Number(v)));
  }

  getGasPrice(readonly = false) {
    return this.web3Execute<number>(readonly, (web3) => web3.eth.getGasPrice()).pipe(map((v) => Number(v)));
  }

  getFeeData(readonly = false) {
    return this.web3Execute<number>(readonly, (web3) => this.calculateFeeData(web3));
  }

  getTransactionCount(address: string, readonly = false) {
    return this.web3Execute<number>(readonly, (web3) => web3.eth.getTransactionCount(address));
  }

  contractAt(address: string, abi: any, readonly = false) {
    return this.web3Execute<any>(readonly, (web3) => of(this.contractAtByWeb3(address, abi, web3)));
  }

  contractAtByWeb3(address: string, abi: any, web3: any) {
    const contract = new web3.eth.Contract(abi);
    contract.options.address = address;
    return contract;
  }

  // toWei(value: any): string {
  //   return this.readOnlyWeb3.utils.toWei(value);
  // }

  // fromWei(value: any): string {
  //   return this.readOnlyWeb3.utils.fromWei(value);
  // }

  switchEthereumChain(params: any) {
    return this.web3Execute<number>(false, (web3) => web3.currentProvider.request({
      method: 'wallet_switchEthereumChain',
      params
    }));
  }

  addEthereumChain(params: any) {
    return this.web3Execute<number>(false, (web3) => web3.currentProvider.request({
      method: 'wallet_addEthereumChain',
      params
    }));
  }

  watchAsset(params: any) {
    return this.web3Execute<number>(false, (web3) => web3.currentProvider.request({
      method: 'wallet_watchAsset',
      params
    }));
  }

  disconnect() {
    this.unwatchBlock();
    this.walletSubject.next(Wallet.None);
    this.softwareSubject.next(Software.None);
    this.chainIdSubject.next(0);
    this.blockNumberSubject.next(0);
    this.statusSubject.next(Status.Disconnected);
    this.web3Subject.next(null);
  }

  connect(wallet: Wallet, options = {}): Observable<any> {
    this.statusSubject.next(Status.Connecting);
    return this.tryWallet(wallet, options).pipe(
      tap(result => {
        result.web3.transactionBlockTimeout = 100000;
        this.web3Subject.next(result.web3);
        this.walletSubject.next(result.wallet);
        this.softwareSubject.next(result.software);
        this.getChainId().subscribe(
          (chainId) => this.setChainId(chainId),
          () => this.blockNumberSubject.next(0)
        );
        this.statusSubject.next(Status.Connected);
        this.watchBlock();
      }),
      catchError(error => {
        console.log(error);
        this.disconnect();
        return throwError(error);
      })
    );
  }

  sendTransaction(transactionObject: any) {
    return this.web3Execute(false, (web3) => web3.eth.sendTransaction(transactionObject));
  }

  estimateGas(transactionObject: any) {
    return this.web3Execute(true, (web3) => web3.eth.estimateGas(transactionObject));
  }

  call(transactionObject: any) {
    return this.web3Execute(true, (web3) => web3.eth.call(transactionObject));
  }

  sign({ message, address, encoding = 'hex' }: { message: any, address: string, encoding: any }): Observable<any> {
    message = '0x' + Buffer.from(message, encoding).toString('hex');
    return this.web3Execute(false, (web3) => web3.eth.personal.sign(message, address, ''));
  }

  signTypedData(account: string, msgParams: any) {
    const params = [account, JSON.stringify(msgParams)];
    return this.web3Execute(false, (web3) => web3.currentProvider.request({
      params,
      method: 'eth_signTypedData_v4',
      from: account
    })).pipe(
      map((data: any) => {
        return {
          r: `0x${data.substring(2, 66)}`,
          s: `0x${data.substring(66, 130)}`,
          v: parseInt(`0x${data.substring(130, 132)}`)
        }
      })
    );
  }

  signAa(tx: any) {
    return this.web3Execute(false, (web3) => {
      const signer = new EIP712Signer(web3, tx.chainId!);
      return signer.sign(tx);
    });
  }

  sendRawTransaction(params: any) {
    return this.web3Execute(false, (web3) => {
      return web3.currentProvider!.request(
        {
          method: 'eth_sendRawTransaction',
          params
        }
      );
    });
  }

  async calculateFeeData(
    web3: any,
    baseFeePerGasFactor: bigint | number = BigInt(2),
    alternativeMaxPriorityFeePerGas = BigInt(1000000000),
  ) {
    const block = await web3.eth.getBlock('latest');
    const baseFeePerGas: bigint | undefined = block?.baseFeePerGas ?? undefined; // use undefined if it was null

    // let gasPrice: bigint | undefined;
    // try {
    //   gasPrice = await web3.eth.getGasPrice();
    // } catch (error) {
    //   // do nothing
    // }

    let maxPriorityFeePerGas: bigint | undefined;
    try {
      maxPriorityFeePerGas = BigInt((await web3.currentProvider.request(
        {
          id: Date.now(),
          jsonrpc: '2.0',
          method: 'eth_maxPriorityFeePerGas'
        }
      )).result);
    } catch (error) {
      // do nothing
    }

    let maxFeePerGas: bigint | undefined;
    // if the `block.baseFeePerGas` is available, then EIP-1559 is supported
    // and we can calculate the `maxFeePerGas` from the `block.baseFeePerGas`
    if (baseFeePerGas) {
      // tip the miner with alternativeMaxPriorityFeePerGas, if no value available from getMaxPriorityFeePerGas
      maxPriorityFeePerGas = maxPriorityFeePerGas ?? alternativeMaxPriorityFeePerGas;
      // basically maxFeePerGas = (baseFeePerGas +- 12.5%) + maxPriorityFeePerGas
      // and we multiply the `baseFeePerGas` by `baseFeePerGasFactor`, to allow
      // trying to include the transaction in the next few blocks even if the
      // baseFeePerGas is increasing fast
      let baseFeeMultiplier: bigint;
      if (typeof baseFeePerGasFactor === 'number') {
        // Convert number to bigint with three decimal places
        baseFeeMultiplier = BigInt(Math.floor(baseFeePerGasFactor * 1000)) / BigInt(1000);
      } else {
        // It's already a BigInt, so just use it as-is
        baseFeeMultiplier = baseFeePerGasFactor;
      }
      maxFeePerGas = baseFeePerGas * baseFeeMultiplier + maxPriorityFeePerGas;
    }

    // return { gasPrice, maxFeePerGas, maxPriorityFeePerGas, baseFeePerGas };
    return { maxFeePerGas, maxPriorityFeePerGas, baseFeePerGas };
  }

  waitForTransactionConfirmed(txid: string = null, confirmation = 1) {
    return interval(1000).pipe(
      concatMap(() => from(this.getTransactionInfo(txid).pipe(catchError(() => EMPTY)))),
      filter((result: any) => result),
      take(1),
      mergeMap(result => forkJoin(this.blockNumber.pipe(
        takeWhile(b => b < Number(result.blockNumber) + confirmation - 1),
        endWith(1)
      ))),
      mergeMap(() => from(this.getTransactionInfo(txid)))
    );
  }

  getAddressUrl(address: string) {
    return `${this.selectedNetworkSubject.value.params.blockExplorerUrls[0]}/address/${address}`;
  }

  getTxUrl(tx: string) {
    return `${this.selectedNetworkSubject.value.params.blockExplorerUrls[0]}/tx/${tx}`;
  }

  private getWeb3(readonly: boolean) {
    return readonly ? this.readonlyWeb3 : this.web3;
  }

  private web3Execute<T>(readonly: boolean, func: (web3: any) => any) {
    return this.getWeb3(readonly).pipe(
      take(1),
      mergeMap((web3) => from(func(web3)))) as Observable<T>;
  }

  private watchBlock() {
    this.unwatchBlock();
    this.getBlockNumber().subscribe(number => this.blockNumberSubject.next(number), e => console.warn(e));
    this.ngZone.runOutsideAngular(() => {
      this.connectWatchBlock = interval(1000).pipe(
        concatMap(() => this.getBlockNumber().pipe(catchError(() => EMPTY))),
        filter(number => number !== this.blockNumberSubject.value)
      ).subscribe(number => {
        this.ngZone.run(() => {
          this.setBlockNumber(number);
        });
      });
    });
  }

  private unwatchBlock() {
    if (this.connectWatchBlock) {
      this.connectWatchBlock.unsubscribe();
      this.connectWatchBlock = null;
    }
  }

  private setBlockNumber(number: number) {
    if (number !== this.blockNumberSubject.value + 1) {
      this.getChainId().subscribe((chainId) => this.setChainId(chainId), error => console.warn(error));
    }
    this.blockNumberSubject.next(number);
  }

  private setChainId(chainId: number) {
    if (this.chainIdSubject.value !== chainId) {
      this.chainIdSubject.next(chainId);
    }
  }

  private createWeb3(options: any = {}) {
    if (options.privateKey) {
      const provider = new HDWalletProvider(options.privateKey, options.host);
      return new Web3(provider);
    } else {
      return new Web3(
        options.host
        // new MultiHttpProvider(options.host)
      );
    }
  }

  private tryWallet(wallet: Wallet, options: any): Observable<any> {
    switch (wallet) {
      case Wallet.PrivateKey:
        return this.tryPrivateKeyWallet(options);
      case Wallet.Default:
        return this.tryDefaultWallet(options);
      case Wallet.Okx:
        return this.tryOkxWallet()
      default:
        return this.trySoftwareWallet();
    }
  }

  private tryPrivateKeyWallet(options: any): Observable<any> {
    if (!options.privateKey || !ethUtil.isValidPrivate(Buffer.from(options.privateKey, 'hex'))) {
      return throwError({ error: Error.InvalidPrivateKey, message: 'invalid private key' });
    }
    return of({
      web3: this.createWeb3(options),
      wallet: Wallet.PrivateKey,
      software: Software.None
    });
  }

  private tryDefaultWallet(options: any): Observable<any> {
    const web3 = this.createWeb3(options);
    return of({
      web3: web3,
      wallet: Wallet.Default,
      software: Software.None
    });
  }

  private trySoftwareWallet(): Observable<any> {
    const win = window as any;
    if (typeof win.ethereum === 'undefined' && typeof win.web3 === 'undefined') {
      return throwError({ error: Error.NoSoftware, message: 'no software wallet found' });
    }

    let source = of(true);
    if (win.ethereum) {
      source = from(win.ethereum.request({ method: 'eth_requestAccounts' })) as Observable<any>;
    }
    return source.pipe(
      map(() => {
        let ethereum = win.ethereum;
        if (ethereum && !('sendAsync' in ethereum)) {
          ethereum = ethereum.target;
        }
        const result = {
          web3: new Web3(ethereum || win.web3.currentProvider),
          wallet: Wallet.Software,
          software: Software.Unknown
        };
        win.test = result.web3;
        if (win.bloctoProvider) {
          result.software = Software.Blocto;
        }
        return result;
      })
    );
  }

  private tryOkxWallet(): Observable<any> {
    const win = window as any;
    if (typeof win.okxwallet === 'undefined') {
      return throwError({ error: Error.NoSoftware, message: 'no okx wallet found' });
    }

    let source = of(true);
    if (win.okxwallet) {
      source = from(win.okxwallet.request({ method: 'eth_requestAccounts' })) as Observable<any>;
    }
    return source.pipe(
      map(() => {
        const result = {
          web3: new Web3(win.okxwallet),
          wallet: Wallet.Okx,
          software: Software.None
        };
        return result;
      })
    );
  }
}
